import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faBuilding, faPhone, faMapMarkerAlt, faCamera, faSave, faTrash, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { businessAuthAPI } from '../../services/api';
import { BusinessType } from '../../types/models';

const ProfileSettings: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('settings');
  const { business, refreshBusinessProfile, updateBusinessProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastProcessedBusinessIdRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    businessName: '',
    category: BusinessType.Other,
    website: '',
    description: '',
    location: {
      address: '',
      city: '',
      country: ''
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch business profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (!business || !business.name) {
        try {
          setFetching(true);
          setError(null);
          await refreshBusinessProfile();
        } catch (err: any) {
          console.error('Failed to fetch business profile:', err);
          setError(err.response?.data?.message || err.message || t('profileSettings.fetchError'));
        } finally {
          setFetching(false);
        }
      }
    };

    fetchProfile();
  }, [business, refreshBusinessProfile, t]);

  // Update form data when business data is available
  useEffect(() => {
    if (business && lastProcessedBusinessIdRef.current !== business._id) {
      lastProcessedBusinessIdRef.current = business._id;

      setFormData(prevFormData => {
        const newFormData = {
          email: business.email || '',
          phone: business.phone || '',
          businessName: business.name || '',
          category: business.category || BusinessType.Other,
          website: business.website || '',
          description: business.description || '',
          location: {
            address: business.location?.address || '',
            city: business.location?.city || '',
            country: business.location?.country || ''
          }
        };

        // Only update if data actually changed
        if (
          prevFormData.email !== newFormData.email ||
          prevFormData.phone !== newFormData.phone ||
          prevFormData.businessName !== newFormData.businessName ||
          prevFormData.category !== newFormData.category ||
          prevFormData.website !== newFormData.website ||
          prevFormData.description !== newFormData.description ||
          prevFormData.location.address !== newFormData.location.address ||
          prevFormData.location.city !== newFormData.location.city ||
          prevFormData.location.country !== newFormData.location.country
        ) {
          return newFormData;
        }
        return prevFormData;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?._id]); // Only update when business ID changes

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData({
        ...formData,
        location: {
          ...formData.location,
          [locationField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('profileSettings.invalidFileType') || 'Invalid file type. Only JPEG, PNG, WebP, and GIF images are allowed.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert(t('profileSettings.fileTooLarge') || 'File size too large. Maximum file size is 5MB.');
      return;
    }

    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append('photo', file);

      const response = await businessAuthAPI.uploadProfilePhoto(formData);
      
      if (response.data.success) {
        // Refresh business profile to get updated photo URL
        await refreshBusinessProfile();
        alert(t('profileSettings.photoUploaded') || 'Profile photo uploaded successfully');
      }
    } catch (err: any) {
      console.error('Failed to upload profile photo:', err);
      alert(err.response?.data?.message || err.message || t('profileSettings.photoUploadError') || 'Failed to upload profile photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePhotoDelete = async () => {
    if (!business?.profilePhoto) {
      alert(t('profileSettings.noPhotoToDelete') || 'No profile photo to delete');
      return;
    }

    if (!window.confirm(t('profileSettings.confirmDeletePhoto') || 'Are you sure you want to delete your profile photo?')) {
      return;
    }

    try {
      setDeletingPhoto(true);
      const response = await businessAuthAPI.deleteProfilePhoto();
      
      if (response.data.success) {
        // Refresh business profile to get updated data
        await refreshBusinessProfile();
        alert(t('profileSettings.photoDeleted') || 'Profile photo deleted successfully');
      }
    } catch (err: any) {
      console.error('Failed to delete profile photo:', err);
      alert(err.response?.data?.message || err.message || t('profileSettings.photoDeleteError') || 'Failed to delete profile photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await updateBusinessProfile({
        name: formData.businessName || business?.name,
        email: formData.email || business?.email,
        phone: formData.phone || business?.phone,
        category: formData.category,
        website: formData.website || business?.website,
        description: formData.description || business?.description,
        location: formData.location.address || formData.location.city || formData.location.country 
          ? formData.location 
          : business?.location
      });
      
      // Reset last processed business ID so formData can be updated with new business data
      lastProcessedBusinessIdRef.current = null;
      
      setSuccess(t('profileSettings.profileUpdated') || 'Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || err.message || t('profileSettings.updateError') || 'Failed to update profile';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white/70">{t('profileSettings.loading') || 'Loading...'}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('profileSettings.title')}</h2>
        <p className="text-white/70">{t('profileSettings.description')}</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Profile Picture Section */}
        <div className="flex items-center space-x-6">
          <div className="relative">
            {business?.profilePhoto ? (
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-purple-400/30">
                <img 
                  src={business.profilePhoto} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to default icon if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML = `
                        <div class="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
                          <svg class="text-white text-2xl" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                          </svg>
                        </div>
                      `;
                    }
                  }}
                />
              </div>
            ) : (
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-white text-2xl" />
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              title={t('profileSettings.changePhoto')}
            >
              {uploadingPhoto ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <FontAwesomeIcon icon={faCamera} className="text-white text-sm" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-1">{t('profileSettings.profilePicture')}</h3>
            <p className="text-white/60 text-sm mb-3">{t('profileSettings.profilePictureDesc')}</p>
            {business?.profilePhoto && (
              <button
                type="button"
                onClick={handlePhotoDelete}
                disabled={deletingPhoto}
                className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
                <span>{deletingPhoto ? t('profileSettings.deleting') || 'Deleting...' : t('profileSettings.deletePhoto') || 'Delete Photo'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
              {t('profileSettings.email')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faEnvelope} className="text-white/40 text-sm" />
              </div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('profileSettings.emailPlaceholder')}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
              {t('profileSettings.phone')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faPhone} className="text-white/40 text-sm" />
              </div>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('profileSettings.phonePlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Business Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="businessName" className="block text-sm font-medium text-white/80 mb-2">
              {t('profileSettings.businessName') || t('businessProfileSettings.businessName') || 'Business Name'}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faBuilding} className="text-white/40 text-sm" />
              </div>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('profileSettings.businessNamePlaceholder') || t('businessProfileSettings.businessNamePlaceholder') || 'Business Name'}
              />
            </div>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-white/80 mb-2">
              {t('profileSettings.category') || t('businessProfileSettings.category') || 'Category'}
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
            >
              <option value={BusinessType.Restaurant}>Restoran</option>
              <option value={BusinessType.Market}>Market</option>
              <option value={BusinessType.Cafe}>Kafe</option>
              <option value={BusinessType.Retail}>Perakende</option>
              <option value={BusinessType.Service}>Hizmet</option>
              <option value={BusinessType.Technology}>Teknoloji</option>
              <option value={BusinessType.Healthcare}>Sağlık</option>
              <option value={BusinessType.Education}>Eğitim</option>
              <option value={BusinessType.Finance}>Finans</option>
              <option value={BusinessType.Manufacturing}>Üretim</option>
              <option value={BusinessType.Other}>Diğer</option>
            </select>
          </div>
        </div>

        {/* Website */}
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-white/80 mb-2">
            {t('profileSettings.website') || t('businessProfileSettings.website') || 'Website'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faGlobe} className="text-white/40 text-sm" />
            </div>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              placeholder={t('profileSettings.websitePlaceholder') || t('businessProfileSettings.websitePlaceholder') || 'https://example.com'}
            />
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('profileSettings.location') || t('businessProfileSettings.location') || 'Location'}
          </label>
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-white/40 text-sm" />
              </div>
              <input
                type="text"
                name="location.address"
                value={formData.location.address}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('profileSettings.address') || t('businessProfileSettings.address') || 'Address'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                name="location.city"
                value={formData.location.city}
                onChange={handleInputChange}
                className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('profileSettings.city') || t('businessProfileSettings.city') || 'City'}
              />
              <input
                type="text"
                name="location.country"
                value={formData.location.country}
                onChange={handleInputChange}
                className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
                placeholder={t('profileSettings.country') || t('businessProfileSettings.country') || 'Country'}
              />
            </div>
          </div>
        </div>

        {/* Description/Bio */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-white/80 mb-2">
            {t('profileSettings.bio') || t('profileSettings.description') || t('businessProfileSettings.description') || 'Description'}
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={4}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 resize-none"
            placeholder={t('profileSettings.bioPlaceholder') || t('profileSettings.descriptionPlaceholder') || t('businessProfileSettings.descriptionPlaceholder') || 'Description'}
          />
          <p className="mt-1 text-xs text-white/50">{t('profileSettings.maxCharacters')}</p>
        </div>

        {/* Save Button */}
        <div className="flex justify-start md:justify-end pt-6 border-t border-white/10">
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-3 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>{t('profileSettings.saving')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="text-sm" />
                <span>{t('profileSettings.saveChanges')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileSettings;
